import { useState, useEffect, useRef, useCallback } from "react";
import PcmAudioRecorder from "../Helpers/realtimeApi/PcmAudioRecorder";
import PcmAudioPlayer from "../Helpers/realtimeApi/PcmAudioPlayer";
import RealtimeConversationClient from "../Helpers/realtimeApi/RealtimeConversationClient";
import { getBaseCode } from "../Helpers/languageHelper";

export const useRealtimeConversation = ({ languageA, languageB, ttsEnabled = true, volume = 1.0, speed = 1.0, user_id }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isListening, setIsListening] = useState(false);

  const [messages, setMessages] = useState(() => {
    const saved = sessionStorage.getItem("live_conversation_history");
    if (saved) {
      try { return JSON.parse(saved); } catch (e) { return []; }
    }
    return [];
  });

  useEffect(() => {
    sessionStorage.setItem("live_conversation_history", JSON.stringify(messages));
  }, [messages]);

  const clearMessages = useCallback(() => {
    setMessages([]);
    sessionStorage.removeItem("live_conversation_history");
  }, []);

  const clientRef = useRef(null);
  const recorderRef = useRef(null);
  const playerRef = useRef(null);
  const micStreamRef = useRef(null);
  const isStartingRef = useRef(false);

  const langARef = useRef(languageA);
  const langBRef = useRef(languageB);
  const ttsEnabledRef = useRef(ttsEnabled);

  useEffect(() => { langARef.current = languageA; }, [languageA]);
  useEffect(() => { langBRef.current = languageB; }, [languageB]);
  useEffect(() => { ttsEnabledRef.current = ttsEnabled; }, [ttsEnabled]);

  const ensureClient = useCallback(() => {
    if (!clientRef.current) {
      clientRef.current = new RealtimeConversationClient({
        onOpen: () => setIsConnected(true),
        onClose: () => setIsConnected(false),
        onError: (err) => console.error("WebSocket Error:", err),
        onRecognizedText: (payload) => {
          setMessages((prevMessages) => {
            const spokenCode = getBaseCode(payload.language);
            const isLangA = spokenCode === getBaseCode(langARef.current);
            const speakerSide = isLangA ? "A" : "B";

            const formattedMessage = {
              id: crypto.randomUUID(),
              user_id: user_id,
              original_message: payload.text,
              language: payload.language,
              speakerSide: speakerSide,
              call_message_translations: []
            };
            return [...prevMessages, formattedMessage];
          });
        },
        onTranslatedText: (payload) => {
          setMessages((prevMessages) => {
            if (prevMessages.length === 0) return prevMessages;
            const newMessages = [...prevMessages];
            const lastIndex = newMessages.length - 1;
            newMessages[lastIndex] = {
              ...newMessages[lastIndex],
              call_message_translations: [{ translation: payload.text }],
              from_lang_code: payload.from_languages?.[0] || payload.language, 
              to_lang_code: payload.to_language
            };
            return newMessages;
          });
        },
        onAudio: (pcmData) => {
          if (playerRef.current && ttsEnabledRef.current) {
            if (!playerRef.current.isInitialized) {
                playerRef.current.initialize().catch(() => {});
            }
            playerRef.current.pushPcmChunk(pcmData);
          }
        }
      });
    }
    return clientRef.current;
  }, [user_id]);

  const ensureAudioPlayer = useCallback(() => {
    if (!playerRef.current) {
      playerRef.current = new PcmAudioPlayer({ initialBufferMs: 200, minChunkMs: 60 });
    }
    return playerRef.current;
  }, []);

  const stopListening = useCallback(() => {
    if (recorderRef.current) {
      recorderRef.current.stop();
      recorderRef.current = null;
    }
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach(track => track.stop());
      micStreamRef.current = null;
    }
    if (clientRef.current) {
      clientRef.current.disconnect();
    }
    if (playerRef.current) {
      playerRef.current.destroy();
      playerRef.current = null;
    }
    setIsListening(false);
  }, []);

  const startListening = async () => {
    if (isStartingRef.current || isListening) return;
    isStartingRef.current = true;

    try {
      if (micStreamRef.current) stopListening();

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      micStreamRef.current = stream;

      const client = ensureClient();
      await client.connect({ languageA, languageB, ttsEnabled, speed });

      recorderRef.current = new PcmAudioRecorder();
      recorderRef.current.start({
        mediaStreamTrack: stream.getAudioTracks()[0],
        onAudio: (pcmChunk) => {
           if (client.isConnected) {
              client.sendAudio(pcmChunk);
           }
        }
      });

      ensureAudioPlayer();
      setIsListening(true);
    } catch (error) {
      console.error("Failed to start listening:", error);
      setIsListening(false);
    } finally {
      isStartingRef.current = false;
    }
  };

  useEffect(() => {
    return () => { stopListening(); };
  }, [stopListening]);

  useEffect(() => {
    if (clientRef.current && clientRef.current.isConnected) {
      clientRef.current.updateConfiguration({ ttsEnabled });
    }
    if (!ttsEnabled && playerRef.current) {
      playerRef.current.stop(); 
    }
  }, [ttsEnabled]);

  useEffect(() => {
    if (clientRef.current && clientRef.current.isConnected) {
      clientRef.current.languageA = languageA;
      clientRef.current.languageB = languageB;
    
    clientRef.current.reconnect().catch(err => console.error(err));
  }
}, [languageA, languageB]);

  useEffect(() => {
    if (playerRef.current) playerRef.current.setVolume(volume);
  }, [volume]);

  useEffect(() => {
    if (clientRef.current && clientRef.current.isConnected) {
      clientRef.current.updateConfiguration({ speed });
    }
  }, [speed]);

  return {
    isConnected,
    isListening,
    messages,
    startListening,
    stopListening,
    clearMessages,
  };
};